import axiosConfig from '@/utils/AxiosConfig';

const SANCTUM_ENDPOINT = '/sanctum/csrf-cookie';

export default {
  /**
   * Attempts to login the user using the provided payload.
   * @param {Object} payload - Object containing the email and password of the user.
   * @returns {Promise<AxiosResponse>} - Axios response containing the result of the login attempt.
   */
  async login(payload) {
    await axiosConfig.auth.get(SANCTUM_ENDPOINT);
    return axiosConfig.auth.post('/auth/login', payload);
  },
  /**
   * Logs out the authenticated user.
   * @returns {Promise<AxiosResponse>} - Axios response containing the result of the logout attempt.
   */
  async logout() {
    return axiosConfig.auth.post('/auth/logout');
  },
  /**
   * Initiates the forgot password process for a user by sending a request
   * to the server with the user's email. The server will handle sending
   * a password reset email to the user.
   *
   * @param {Object} payload - Object containing the user's email.
   * @returns {Promise<AxiosResponse>} - Axios response containing the result of the forgot password attempt.
   */
  async forgotPassword(payload) {
    await axiosConfig.auth.get(SANCTUM_ENDPOINT);
    return axiosConfig.api.post('/password/forgot-password', payload);
  },
  /**
   * Fetches the authenticated user's details.
   *
   * @returns {Promise} A promise that resolves to the user's information.
   */
  user() {
    return axiosConfig.api.get('/auth/user');
  },
  /**
   * Resets the password for a user given the password reset token
   * and a new password.
   *
   * @param {Object} payload - Object containing the user's email,
   * password and password reset token.
   * @returns {Promise<AxiosResponse>} - Axios response containing the result of the password reset attempt.
   */
  async resetPassword(payload) {
    await axiosConfig.auth.get(SANCTUM_ENDPOINT);
    return axiosConfig.api.post('/password/reset-password', payload);
  },
  /**
   * Updates the password for the authenticated user.
   *
   * @param {Object} payload - Object containing the current password,
   * new password, and password confirmation.
   * @returns {Promise<AxiosResponse>} - Axios response containing the result of the password update attempt.
   */
  updatePassword(payload) {
    return axiosConfig.auth.put('/user/password', payload);
  },
  /**
   * Registers a new user with the provided payload.
   * Sends a request to obtain the CSRF cookie before making the registration request.
   *
   * @param {Object} payload - Object containing user registration details.
   * @returns {Promise<AxiosResponse>} - Axios response containing the result of the registration attempt.
   */
  async register(payload) {
    await axiosConfig.auth.get(SANCTUM_ENDPOINT);
    return axiosConfig.auth.post('/auth/register', payload);
  },
  /**
   * Sends a verification email to the user if the user's email is not verified.
   *
   * @param {Object} payload - Object containing the user's email.
   * @returns {Promise<AxiosResponse>} - Axios response containing the result of the email verification attempt.
   */
  sendVerification(payload) {
    return axiosConfig.api.post('/emails/verification-notification', payload);
  },
  /**
   * Verifies the user's email address using the provided ID and hash.
   * The verification is performed by sending a GET request to the server.
   *
   * @param {string} id - The unique identifier of the user.
   * @param {string} hash - The verification hash associated with the user's email.
   * @param {string} query - Additional query parameters, if any.
   * @returns {Promise<AxiosResponse>} - Axios response containing the result of the email verification attempt.
   */
  verifyEmail(id, hash, query) {
    return axiosConfig.api.get(`/emails/verify/${id}/${hash}?${query}`);
  },
  /**
   * Updates the user's profile information with the provided payload.
   *
   * @param {Object} payload - Object containing the user's profile details to be updated.
   * @returns {Promise<AxiosResponse>} - Axios response containing the result of the profile update attempt.
   */
  updateUser(payload) {
    return axiosConfig.auth.put('/user/profile-information', payload);
  },
};
